<template>
  <!-- Recherche-Anzeige: die Schritte folgen den status-Ereignissen des
       Servers (knowledge → context → thinking → writing). Erledigte Schritte
       bekommen ein Häkchen, der aktuelle zeigt laufende Punkte. -->
  <div aria-live="polite" class="ai-thinking ai-rise" role="status">
    <span aria-hidden="true" class="ai-mark ai-thinking__mark">KI</span>
    <ol class="ai-thinking__steps">
      <li
        v-for="(item, index) in steps"
        :key="item.key"
        class="ai-thinking__step"
        :class="{ 'is-done': index < currentIndex, 'is-current': index === currentIndex, 'is-pending': index > currentIndex }"
      >
        <span aria-hidden="true" class="ai-thinking__state">
          <Check v-if="index < currentIndex" :size="12" />
          <span v-else-if="index === currentIndex" class="ai-thinking__dots"><i /><i /><i /></span>
        </span>
        <span>{{ index === currentIndex && stepText ? stepText : item.label }}</span>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
  import type { AssistantStep } from '@/services/assistantApi'
  import { Check } from 'lucide-vue-next'
  import { computed } from 'vue'
  import { gt } from '@/i18n/gt'

  const props = defineProps<{
    step: AssistantStep | null
    stepText?: string
  }>()

  const steps = computed(() => [
    { key: 'knowledge', label: gt('Durchsuche das Hilfe-Center') },
    { key: 'context', label: gt('Lese Ihre Einstellungen und Aufgaben') },
    { key: 'thinking', label: gt('Formuliere die Antwort') },
  ])

  const currentIndex = computed(() => {
    const order: AssistantStep[] = ['knowledge', 'context', 'thinking', 'writing']
    const idx = props.step ? order.indexOf(props.step) : 0
    return Math.min(Math.max(idx, 0), steps.value.length)
  })
</script>

<style scoped>
  .ai-thinking {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    max-width: 88%;
    padding: 12px 14px;
    border: 1px solid var(--ai-line);
    border-radius: 12px;
    background: var(--ai-surface);
  }

  .ai-thinking__mark {
    flex: none;
    width: 26px;
    height: 26px;
    margin-top: 1px;
  }

  .ai-thinking__steps {
    display: grid;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 13px;
  }

  .ai-thinking__step {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--ai-muted);
    transition: opacity 200ms ease;
  }

  .ai-thinking__step.is-pending {
    opacity: 0.45;
  }

  .ai-thinking__step.is-current {
    color: var(--ai-ink);
    font-weight: 500;
  }

  .ai-thinking__state {
    display: inline-grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--ai-surface-3);
    color: var(--ai-success);
  }

  .is-done .ai-thinking__state {
    background: var(--ai-success-soft);
    animation: ai-pop 280ms var(--ai-ease) both;
  }

  .ai-thinking__dots {
    display: inline-flex;
    gap: 2px;
  }

  .ai-thinking__dots i {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--ai-blue);
    animation: ai-dot 1.1s ease-in-out infinite;
  }

  .ai-thinking__dots i:nth-child(2) {
    animation-delay: 0.15s;
  }

  .ai-thinking__dots i:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-thinking__dots i,
    .is-done .ai-thinking__state {
      animation: none !important;
    }
  }
</style>
